import React, { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import getAmountOfGlassAreas from 'components/configurator/services/getAmountOfGlassAreas';
import getGlassAreaLength from 'components/configurator/services/getGlassAreaLength';
import getTargetName from 'services/forms/getTargetName';
import getTargetValue from 'services/forms/getTargetValue';
import glassTreatmentTypes from 'services/constants/glassTreatmentTypes';
import glassPropertyTypes from 'services/constants/glassPropertyTypes';
import glassEdgeTreatmentTypes from 'services/constants/glassEdgeTreatmentTypes';
import IGlassArea from 'components/interfaces/IGlassArea';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import IWall from 'components/interfaces/IWall';
import NumberInputField from 'components/layout/forms/NumberInputField';
import roundTwoDecimals from 'services/numbers/roundTwoDecimals';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TextInputField from 'components/layout/forms/TextInputField';
import translations from 'services/translations/translations';
import { DEFAULT_MAX_WEIGHT } from 'services/constants/glassWidthCalculationDefaultValues';
import { setWallMetaData } from 'store/selectedWall/actions';

import glassWidthCalculationTypes, {
  MANUAL,
  MAX_WEIGHT,
  QUANTITY,
} from 'services/constants/glassWidthCalculationTypes';

import getDefaultGlass from './getDefaultGlass';
import getFieldErrorMessage from '../getFieldErrorMessage';
import GlassesList from './GlassesList';
import validateGlassArea from '../validateGlassArea';

import 'style/configurator/modals/src/glassWall/glassAreas/GlassProperties.css';

const GlassProperties = ({
  doors,
  glassAreas,
  setGlassAreas,
  wall,
  disableInputs,
}: {
  doors: IGlassWallDoor[];
  glassAreas: IGlassArea[];
  setGlassAreas: (areas: IGlassArea[]) => void;
  wall: IWall;
  disableInputs?: boolean;
}): JSX.Element | null => {
  const dispatch = useDispatch();

  const wallMetaData = useSelector((state: any) => state.selectedWall.metaData);

  const { t } = useTranslation([
    translations.common,
    translations.glassWallGlassProperties,
  ]);

  const [selectedGlassArea, setSelectedGlassArea] = useState(0);
  const [glassAreaOptions, setGlassAreaOptions] = useState<number[]>([]);

  const maxManualQuantity = 20;

  const errorMessages = validateGlassArea(
    glassAreas[selectedGlassArea],
    wallMetaData.glassAreas[selectedGlassArea],
    selectedGlassArea,
    wall
  );

  const quantityError = getFieldErrorMessage('quantity', errorMessages);
  const maxWeightError = getFieldErrorMessage('maxWeight', errorMessages);

  useEffect(() => {
    const amountOfGlassAreas = getAmountOfGlassAreas(doors, wall);
    const newGlassAreaOptions = [];
    for (let i = 0; i < amountOfGlassAreas; ++i) {
      newGlassAreaOptions.push(i);
    }

    setGlassAreaOptions(newGlassAreaOptions);
  }, [doors]);

  console.log('yyyyyy', glassAreas, selectedGlassArea);

  const glassArea = glassAreas[selectedGlassArea];
  const glassAreaLength = getGlassAreaLength(selectedGlassArea, wall);
  const glassAreaMetaData = wallMetaData.glassAreas[selectedGlassArea];

  const setGlassArea = (updatedGlassArea: IGlassArea) => {
    setGlassAreas(
      glassAreas.map((existingGlassArea, index) =>
        index === selectedGlassArea ? updatedGlassArea : existingGlassArea
      )
    );
  };

  const handleGlassArea = (e: any) => {
    const targetName = getTargetName(e);

    setGlassArea({
      ...glassArea,
      [targetName]: getTargetValue(e),
    });
  };

  const handleGlassAreaMetaData = (e: any) => {
    const targetName = getTargetName(e);
    const targetValue = getTargetValue(e);

    const updatedGlassAreaMetaData = {
      ...glassAreaMetaData,
      [targetName]: targetValue,
    };

    if (targetName === 'calculationType') {
      if (targetValue === MAX_WEIGHT) {
        if (updatedGlassAreaMetaData.maxWeight === null) {
          updatedGlassAreaMetaData.maxWeight = DEFAULT_MAX_WEIGHT;
        }
      }

      if (targetValue === QUANTITY) {
        if (updatedGlassAreaMetaData.quantity === null) {
          updatedGlassAreaMetaData.quantity = 1;
        }
      }
    }

    const newWallMetaDataGlassAreas = [...wallMetaData.glassAreas];
    newWallMetaDataGlassAreas[selectedGlassArea] = updatedGlassAreaMetaData;

    dispatch(
      setWallMetaData({
        ...wallMetaData,
        glassAreas: newWallMetaDataGlassAreas,
      })
    );
  };

  const handleManualQuantity = (event: any) => {
    if (getTargetValue(event) === 0) {
      return;
    }

    const nextGlasses = [...glassArea.glasses];
    while (nextGlasses.length < getTargetValue(event)) {
      if (nextGlasses.length >= maxManualQuantity) {
        break;
      }

      nextGlasses.push(getDefaultGlass());
    }

    while (nextGlasses.length > getTargetValue(event)) {
      glassArea.glasses.pop();
    }

    setGlassAreas(
      glassAreas.map((existingGlassArea, index) =>
        index === selectedGlassArea
          ? {
              ...existingGlassArea,
              glasses: nextGlasses,
            }
          : existingGlassArea
      )
    );
  };

  const onSelectedGlassAreaChange = (event: any) => {
    setSelectedGlassArea(parseInt(event?.target.value, 10));
  };

  if (glassAreaOptions.length === 0) {
    return null;
  }

  return (
    <>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="glass-area-select"
            onChange={onSelectedGlassAreaChange}
            options={glassAreaOptions.map((glassAreaOptionId) => (
              <option
                key={`glass-area-${glassAreaOptionId}`}
                value={glassAreaOptionId}
              >
                {t(`${translations.glassWallGlassProperties}:Glass area`)}
                {glassAreaOptionId + 1}
              </option>
            ))}
            required
            value={selectedGlassArea}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInputField
            id="name"
            label={t(`${translations.common}:Name`)}
            onChange={handleGlassArea}
            value={glassArea.name}
            name="name"
            required
            disabled={disableInputs}
          />
        </Col>
        {wall.onlyDoor ? null : (
          <Col>
            <TextInputField
              disabled
              id="length"
              label={t(`${translations.common}:Length`)}
              name="length"
              value={roundTwoDecimals(glassAreaLength)}
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          <SelectInputFieldControlled
            id="glass-edge-treatment-type"
            label={t(`${translations.glassWallGlassProperties}:Edge treatment`)}
            name="edgeTreatmentType"
            onChange={handleGlassAreaMetaData}
            options={glassEdgeTreatmentTypes.map((type) => (
              <option key={`edge-treatment-type-${type}`} value={type}>
                {t(
                  `${translations.glassWallGlassProperties}:edgeTreatmentTypes:${type}`
                )}
              </option>
            ))}
            required
            value={glassAreaMetaData.edgeTreatmentType}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="glass-property-type"
            label={t(`${translations.glassWallGlassProperties}:Glass property`)}
            name="glassPropertyType"
            onChange={handleGlassAreaMetaData}
            options={glassPropertyTypes.map((type) => (
              <option key={`glass-property-type-${type}`} value={type}>
                {t(
                  `${translations.glassWallGlassProperties}:glassPropertyTypes:${type}`
                )}
              </option>
            ))}
            required
            value={glassAreaMetaData.glassPropertyType}
            disabled={disableInputs}
          />
        </Col>
        <Col>
          <SelectInputFieldControlled
            id="glass-treatment-type"
            label={t(
              `${translations.glassWallGlassProperties}:Glass treatment`
            )}
            name="glassTreatmentType"
            onChange={handleGlassAreaMetaData}
            options={glassTreatmentTypes.map((type) => (
              <option key={`glass-treatment-type-${type}`} value={type}>
                {t(
                  `${translations.glassWallGlassProperties}:glassTreatmentTypes:${type}`
                )}
              </option>
            ))}
            required
            value={glassAreaMetaData.glassTreatmentType}
            disabled={disableInputs}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInputField
            id="notice"
            label={t(`${translations.glassWallGlassProperties}:Notice`)}
            onChange={handleGlassAreaMetaData}
            value={glassAreaMetaData.notice}
            name="notice"
            required
            disabled={disableInputs}
          />
        </Col>
      </Row>
      {wall.onlyDoor ? null : (
        <Row>
          <Col>
            <SelectInputFieldControlled
              id="glass-width-calculation-type"
              label={t(
                `${translations.glassWallGlassProperties}:Glass width calculation`
              )}
              name="calculationType"
              onChange={handleGlassAreaMetaData}
              options={glassWidthCalculationTypes.map((type) => (
                <option
                  key={`glass-width-calculation-type-${type}`}
                  value={type}
                >
                  {t(
                    `${translations.glassWallGlassProperties}:glassWidthCalculationTypes:${type}`
                  )}
                </option>
              ))}
              required
              value={glassAreaMetaData.calculationType}
              disabled={disableInputs}
            />
          </Col>
        </Row>
      )}
      {!wall.onlyDoor && glassAreaMetaData.calculationType === MAX_WEIGHT ? (
        <Row>
          <Col>
            <NumberInputField
              error={maxWeightError}
              id="max-weight"
              label={t(
                `${translations.glassWallGlassProperties}:Max weight (kg)`
              )}
              name="maxWeight"
              onChange={handleGlassAreaMetaData}
              value={glassAreaMetaData.maxWeight}
              disabled={disableInputs}
            />
          </Col>
        </Row>
      ) : null}
      {!wall.onlyDoor && glassAreaMetaData.calculationType === QUANTITY ? (
        <Row>
          <Col>
            <NumberInputField
              error={quantityError}
              id="glass-quantity"
              label={t(`${translations.glassWallGlassProperties}:Quantity`)}
              name="quantity"
              onChange={handleGlassAreaMetaData}
              value={glassAreaMetaData.quantity}
              disabled={disableInputs}
            />
          </Col>
        </Row>
      ) : null}
      {!wall.onlyDoor && glassAreaMetaData.calculationType === MANUAL ? (
        <Row>
          <Col>
            <NumberInputField
              id="glass-manual-quantity"
              label={t(`${translations.glassWallGlassProperties}:Quantity`)}
              name="manualQuantity"
              onChange={handleManualQuantity}
              value={glassArea.glasses.length}
              disabled={disableInputs}
            />
          </Col>
        </Row>
      ) : null}
      {wall.onlyDoor ? null : (
        <GlassesList
          glassArea={glassArea}
          glassAreaLength={glassAreaLength}
          glassAreaMetaData={glassAreaMetaData}
          setGlassArea={setGlassArea}
          wall={wall}
          disabledInputs={disableInputs}
        />
      )}
    </>
  );
};

export default GlassProperties;
